import { useCallback, useEffect, useRef } from 'react';

import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { Notification } from '../../types';
import { NotificationItem } from '../notification-item';

interface NotificationListProps {
  notifications: Notification[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: Error | null;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  onMarkAsRead: (notificationId: number) => void;
  onDelete: (notificationId: number) => void;
  onRetry: () => void;
  emptyTitle: string;
  emptyDescription: string;
}

/**
 * Skeleton loader for notification items.
 */
function NotificationSkeleton() {
  return (
    <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-12" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

/**
 * Error state component.
 */
function NotificationError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('authenticated.notifications');

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-8 text-center">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-medium text-foreground">{t('inbox.error.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('inbox.error.description')}</p>
      </div>
      <Button variant="default" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-4 w-4" />
        {t('inbox.error.retry')}
      </Button>
    </div>
  );
}

/**
 * Empty state component.
 */
function NotificationEmpty({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-8 text-center">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/**
 * List component for displaying notifications with infinite scroll.
 * Handles loading, error, and empty states.
 */
export function NotificationList({
  notifications,
  isLoading,
  isFetchingNextPage,
  error,
  hasNextPage,
  fetchNextPage,
  onMarkAsRead,
  onDelete,
  onRetry,
  emptyTitle,
  emptyDescription,
}: NotificationListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!containerRef.current || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    // Load more when user is 100px from bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Initial loading state
  if (isLoading && notifications.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto">
        {Array.from({ length: 5 }).map((_, index) => (
          <NotificationSkeleton key={index} />
        ))}
      </div>
    );
  }

  // Error state
  if (error && notifications.length === 0) {
    return <NotificationError onRetry={onRetry} />;
  }

  // Empty state
  if (notifications.length === 0) {
    return <NotificationEmpty title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onMarkAsRead={onMarkAsRead}
          onDelete={onDelete}
        />
      ))}
      {isFetchingNextPage && (
        <div className="py-4">
          <NotificationSkeleton />
        </div>
      )}
    </div>
  );
}
